// The main chat the server knows (TOM-517, cloud ADR 0337, contract §12.19):
// one conversation per owner per boat, the same in every tab and on every
// device. Home's chat, the Neo page and the floating panel ask the server for
// it instead of keeping an id of their own, and Neomaris posts an alarm's
// message into it. New chat opens a separate one at `neo/new`.
//
// One request per boat per page load: the hosts on a page share it. A main
// chat deleted from Chats is forgotten here, and the next ask makes a new one.

import { api } from '../api';

/** The window event a `neo_main_message` registry frame becomes (contract §8). */
export const NEO_MAIN_MESSAGE_EVENT = 'nm:neo-main-message';

export interface NeoMainMessageDetail {
  boatId: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The main chat's id from a §12.19 answer, or null when the answer is not one. */
export function parseMainNeoConversation(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const id = (body as { conversationId?: unknown }).conversationId;
  return typeof id === 'string' && UUID.test(id) ? id : null;
}

const asked = new Map<string, Promise<string | null>>();

/**
 * The owner's main chat on the boat, made by the server when there is none.
 * Null when it cannot be had (offline, an old server): the chat then starts a
 * separate conversation on its first message, as before.
 */
export function fetchMainNeoConversation(boatId: string): Promise<string | null> {
  const pending = asked.get(boatId);
  if (pending !== undefined) return pending;
  const request = api<unknown>('/v1/neo/main-chat', {
    method: 'POST',
    body: { boatId },
  })
    .then(parseMainNeoConversation)
    .catch(() => null)
    .then((id) => {
      // A failure is asked again next time, not remembered.
      if (id === null) asked.delete(boatId);
      return id;
    });
  asked.set(boatId, request);
  return request;
}

/** Forget the boat's main chat: it was deleted, so the next ask makes a new one. */
export function forgetMainNeoConversation(boatId: string): void {
  asked.delete(boatId);
}

/** Announce a `neo_main_message` frame to the page's main-chat hosts. */
export function announceNeoMainMessage(boatId: string): void {
  window.dispatchEvent(
    new CustomEvent<NeoMainMessageDetail>(NEO_MAIN_MESSAGE_EVENT, { detail: { boatId } })
  );
}

/**
 * The window event for a greeting being asked for (TOM-920): the main chat says
 * Neo is checking on the boat until a greeting it had not seen arrives, or
 * until the event says nothing was queued (`pending: false`).
 */
export const NEO_GREETING_PENDING_EVENT = 'nm:neo-greeting-pending';

export interface NeoGreetingPendingDetail {
  boatId: string;
  pending: boolean;
}

/** How long a chat waits for a queued greeting before it stops saying so. */
export const NEO_GREETING_WAIT_MS = 60_000;

/** Tell the page's main-chat hosts a greeting is being asked for, or that none was queued. */
export function announceNeoGreetingPending(boatId: string, pending = true): void {
  window.dispatchEvent(
    new CustomEvent<NeoGreetingPendingDetail>(NEO_GREETING_PENDING_EVENT, {
      detail: { boatId, pending },
    })
  );
}

/** The address of a separate new chat on the boat (ADR 0337 §3). */
export function newNeoChatPath(boatId: string): string {
  return `/dashboard/boats/${boatId}/neo/new`;
}
